import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import { Action } from '@ngrx/store';
import { Profile, UserInfo, UserRole } from '@tekman/onmat/models';
import { BurnedLicensesService } from '@tekman/onmat/service/generic';
import { StorageService } from '@tekman/shared/service';
import { EMPTY, Observable, of } from 'rxjs';
import { ProfileService } from '../service/profile.service';
import * as ProfileActions from './profile.actions';

@Injectable()
export class ProfileEffects {
  loadProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.loadProfile),
      switchMap(({ authProfile }) =>
        this.profileService.getProfile().pipe(
          switchMap((userInfo: UserInfo) => {
            if (authProfile?.roles?.includes('STUDENT') && !!userInfo?.teamId) this.storageService.teamId = userInfo?.teamId;
            const profile = new Profile(authProfile);
            // The profile is not published until it is known whether the student
            // has the license burned, so the app renders nothing until then.
            return this.ensureOnmatLicense(profile, authProfile?.attributes?.['courseId']).pipe(
              map(() => ProfileActions.loadProfileSuccess({ profile, userInfo }))
            );
          }),
          catchError((error: HttpErrorResponse) => of(ProfileActions.loadProfileFailure({ error: { message: error.message } })))
        )
      )
    )
  );

  removeProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.removeProfile),
      switchMap(() => {
        this.storageService.removeSelectedGroupId();
        return new Observable<Action>();
      })
    )
  );

  /**
   * Resolves once the user is allowed to stay inside ONMAT. An ESP student with
   * no burned ONMAT license for their own course is sent to Tekman Digital to
   * activate it (the burn flow lives there) and this never emits, so the profile
   * is never published and the app renders nothing while the browser leaves.
   *
   * The region comes from the academic year id `LicensesGuard` has already
   * resolved from the license catalog. Teachers/admins and every other region
   * have all their levels enabled and never burn codes, so they pass straight
   * through.
   */
  private ensureOnmatLicense(profile: Profile, courseId: string): Observable<void> {
    const isStudent = profile?.roles?.includes(UserRole.student);
    if (!isStudent || !this.burnedLicensesService.isEspAcademicYear(this.storageService.selectedAcademicYearId)) {
      return of(undefined);
    }
    return this.burnedLicensesService.getBurnedByUser().pipe(
      switchMap(licenses => {
        if (this.burnedLicensesService.hasOnmatLicense(licenses, courseId)) {
          return of(undefined);
        }
        this.burnedLicensesService.redirectToActivation();
        return EMPTY;
      }),
      // A failing core is no proof the license is missing: the student is left
      // inside rather than kicked out over a one-off error.
      catchError(() => of(undefined))
    );
  }

  constructor(
    private actions$: Actions,
    private profileService: ProfileService,
    private readonly storageService: StorageService,
    private readonly burnedLicensesService: BurnedLicensesService
  ) {}
}
